import React from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TextFieldsRoundedIcon from "@mui/icons-material/TextFieldsRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import FormatSizeRoundedIcon from "@mui/icons-material/FormatSizeRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import ShuffleRoundedIcon from "@mui/icons-material/ShuffleRounded";
import EditableImage from "../landingEditor/EditableImage";
import {
  getSegmentVisualStyle,
  SEGMENT_LABELS,
  updateSegmentVisualStyle,
} from "./segmentVisualStyle";

const TAB_INDEX = { content: 0, colors: 1, style: 2, type: 3, image: 4 };
const INDEX_TAB = ["content", "colors", "style", "type", "image"];

const FONT_PRESETS = [
  {
    id: "friendly",
    label: "Cercana",
    heading: "Exo 2",
    body: "Asap",
    url: "https://fonts.googleapis.com/css2?family=Asap:wght@400;500;600;700&family=Exo+2:wght@600;700;800;900&display=swap",
  },
  {
    id: "modern",
    label: "Moderna",
    heading: "Poppins",
    body: "Inter",
    url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@600;700;800;900&display=swap",
  },
  {
    id: "strong",
    label: "Comercial",
    heading: "Montserrat",
    body: "Open Sans",
    url: "https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800;900&family=Open+Sans:wght@400;500;600;700&display=swap",
  },
  {
    id: "elegant",
    label: "Elegante",
    heading: "Playfair Display",
    body: "Lato",
    url: "https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&family=Playfair+Display:wght@600;700;800;900&display=swap",
  },
];

const COLOR_DEFAULTS = {
  primaryColor: "#F35A3A",
  secondaryColor: "#19242F",
  surfaceColor: "#FFFFFF",
  backgroundColor: "#EEF0F5",
  textColor: "#1A1A2E",
  mutedTextColor: "#5B5D73",
};

const PALETTE_LABELS = [
  "Color 1",
  "Color 2",
  "Color 3",
  "Color 4",
  "Color 5",
];

function paletteFromForm(form) {
  return [
    form?.theme?.primaryColor || COLOR_DEFAULTS.primaryColor,
    form?.theme?.secondaryColor || COLOR_DEFAULTS.secondaryColor,
    form?.theme?.surfaceColor || COLOR_DEFAULTS.surfaceColor,
    form?.theme?.backgroundColor || COLOR_DEFAULTS.backgroundColor,
    form?.theme?.textColor || COLOR_DEFAULTS.textColor,
  ].map(toColorInput);
}

function paletteChanges(sectionId, palette) {
  const [primary, secondary, surface, background, text] = palette.map(toColorInput);
  const globalChanges = {
    "theme.primaryColor": primary,
    "theme.secondaryColor": secondary,
    "theme.surfaceColor": surface,
    "theme.backgroundColor": background,
    "theme.textColor": text,
    "theme.mutedTextColor": text,
  };

  if (["about", "highlights", "contact"].includes(sectionId)) {
    const base = `appearance.${sectionId}`;
    return {
      ...globalChanges,
      [`${base}.backgroundColor`]: background,
      [`${base}.surfaceColor`]: surface,
      [`${base}.accentColor`]: primary,
      [`${base}.titleColor`]: secondary,
      [`${base}.textColor`]: text,
    };
  }

  if (sectionId === "spotlight") {
    return {
      ...globalChanges,
      "catalog.spotlight.primaryColor": primary,
      "catalog.spotlight.secondaryColor": secondary,
    };
  }

  return globalChanges;
}

function hexRgb(color) {
  const value = toColorInput(color).slice(1);
  return [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
}

function relativeLuminance(color) {
  const channels = hexRgb(color).map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function contrastRatio(first, second) {
  const light = Math.max(relativeLuminance(first), relativeLuminance(second));
  const dark = Math.min(relativeLuminance(first), relativeLuminance(second));
  return (light + 0.05) / (dark + 0.05);
}

function palettePermutations(values) {
  if (values.length <= 1) return [values];
  return values.flatMap((value, index) =>
    palettePermutations(values.filter((_, current) => current !== index)).map((rest) => [value, ...rest])
  );
}

function randomizedReadablePalette(palette) {
  const values = palette.map(toColorInput);
  const readable = palettePermutations(values).filter((candidate) =>
    contrastRatio(candidate[3], candidate[4]) >= 3 &&
    contrastRatio(candidate[2], candidate[4]) >= 3
  );
  const options = readable.length ? readable : palettePermutations(values);
  if (options.length <= 1) return values;

  const currentKey = values.join("|");
  const alternatives = options.filter((candidate) => candidate.join("|") !== currentKey);
  const candidates = alternatives.length ? alternatives : options;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

const SECTION_VISIBILITY_PATHS = {
  navbar: "sections.showNavbar",
  hero: "sections.showHero",
  spotlight: "sections.showSpotlight",
  featured: "sections.showFeatured",
  brands: "sections.showBrands",
  categories: "sections.showCategories",
  about: "sections.showAbout",
  highlights: "sections.showHighlights",
  contact: "sections.showContact",
  footer: "sections.showFooter",
  floatingWhatsapp: "sections.showFloatingWhatsapp",
};

function getAtPath(source, path, fallback = "") {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, key) => value?.[key], source) ?? fallback;
}

function colorFieldsForSection(sectionId) {
  if (["about", "highlights", "contact"].includes(sectionId)) {
    const base = `appearance.${sectionId}`;
    return [
      { path: `${base}.backgroundColor`, label: "Fondo de la sección", fallbackPath: "theme.backgroundColor" },
      { path: `${base}.surfaceColor`, label: "Tarjetas o superficie", fallbackPath: "theme.surfaceColor" },
      { path: `${base}.accentColor`, label: "Color destacado", fallbackPath: "theme.primaryColor" },
      { path: `${base}.titleColor`, label: "Títulos", fallbackPath: "theme.secondaryColor" },
      { path: `${base}.textColor`, label: "Textos", fallbackPath: "theme.mutedTextColor" },
    ];
  }

  if (sectionId === "spotlight") {
    return [
      { path: "catalog.spotlight.primaryColor", label: "Color principal", fallbackPath: "theme.primaryColor" },
      { path: "catalog.spotlight.secondaryColor", label: "Color secundario", fallbackPath: "theme.secondaryColor" },
    ];
  }

  return [
    { path: "theme.primaryColor", label: "Color principal", fallback: COLOR_DEFAULTS.primaryColor },
    { path: "theme.secondaryColor", label: "Color oscuro y títulos", fallback: COLOR_DEFAULTS.secondaryColor },
    { path: "theme.surfaceColor", label: "Superficie clara", fallback: COLOR_DEFAULTS.surfaceColor },
    { path: "theme.backgroundColor", label: "Fondo general", fallback: COLOR_DEFAULTS.backgroundColor },
    { path: "theme.textColor", label: "Texto principal", fallback: COLOR_DEFAULTS.textColor },
    { path: "theme.mutedTextColor", label: "Texto secundario", fallback: COLOR_DEFAULTS.mutedTextColor },
  ];
}

function toColorInput(value) {
  const candidate = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(candidate)) return candidate;
  if (/^#[0-9a-fA-F]{3}$/.test(candidate)) {
    return `#${candidate.slice(1).split("").map((char) => char + char).join("")}`;
  }
  const rgb = candidate.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) return `#${rgb.slice(1, 4).map((part) => Math.max(0, Math.min(255, Number(part))).toString(16).padStart(2, "0")).join("")}`;
  return "#000000";
}

function imageFieldsForSection(sectionId, form) {
  switch (sectionId) {
    case "navbar":
    case "footer":
      return [
        { path: "theme.logoUrl", label: "Logo principal", key: "logo" },
        { path: "theme.compactLogoUrl", label: "Logo compacto para celular", key: "compact-logo" },
      ];
    case "hero": {
      const hasFirstSlide = Boolean(form.hero?.slides?.[0]?.url);
      return [{
        path: hasFirstSlide ? "hero.slides.0.url" : "hero.imageUrl",
        label: "Imagen de fondo de la portada",
        key: hasFirstSlide ? "slide-1" : "background",
        positionPath: hasFirstSlide ? "hero.slides.0.position" : "hero.imagePosition",
      }];
    }
    case "spotlight":
      return [
        { path: "catalog.spotlight.backgroundImageUrl", label: "Imagen de fondo", key: "background" },
        { path: "catalog.spotlight.logoUrl", label: "Logo o imagen principal", key: "logo" },
      ];
    case "brands":
      return (form.catalog?.brands?.items || []).slice(0, 6).map((item, index) => ({
        path: `catalog.brands.items.${index}.logoUrl`,
        label: item.name || `Marca ${index + 1}`,
        key: `brand-${index + 1}`,
      }));
    case "about":
      return [{ path: "about.imageUrl", label: "Imagen del negocio", key: "main", positionPath: "about.imagePosition" }];
    case "highlights":
      return (form.highlights || []).slice(0, 3).map((item, index) => ({
        path: `highlights.${index}.imageUrl`,
        label: item.title || `Diferencial ${index + 1}`,
        key: `highlight-${index + 1}`,
      }));
    case "contact":
      return [{ path: "contact.backgroundImageUrl", label: "Imagen o mapa de contacto", key: "background" }];
    default:
      return [];
  }
}

function LevelSelector({ label, value, options, onChange }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 900 }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={value}
        onChange={(_, next) => next && onChange(next)}
      >
        {options.map((option) => (
          <ToggleButton key={option.value} value={option.value} sx={{ textTransform: "none", fontWeight: 850 }}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

export default function LandingSegmentInspector({
  open,
  sectionId,
  initialTool = "content",
  form,
  productsEnabled,
  saving,
  onClose,
  onCommit,
  onCommitMany,
  onOpenTexts,
  onUploadImage,
  onRemoveImage,
}) {
  const mobile = useMediaQuery("(max-width:760px)");
  const [tab, setTab] = React.useState(TAB_INDEX[initialTool] || 0);
  const [colorDraft, setColorDraft] = React.useState({});
  const [paletteDraft, setPaletteDraft] = React.useState(() => paletteFromForm(form));
  const label = SEGMENT_LABELS[sectionId] || "Sección";
  const visual = getSegmentVisualStyle(form.theme, sectionId);
  const colorFields = React.useMemo(() => colorFieldsForSection(sectionId), [sectionId]);
  const imageFields = React.useMemo(() => imageFieldsForSection(sectionId, form), [form, sectionId]);
  const visibilityPath = SECTION_VISIBILITY_PATHS[sectionId];
  const visible = visibilityPath ? getAtPath(form, visibilityPath, true) !== false : true;

  React.useEffect(() => {
    if (open) setTab(TAB_INDEX[initialTool] || 0);
  }, [initialTool, open, sectionId]);

  React.useEffect(() => {
    const next = {};
    colorFields.forEach((field) => {
      const fallback = field.fallbackPath
        ? getAtPath(form, field.fallbackPath, "#000000")
        : field.fallback || "#000000";
      next[field.path] = getAtPath(form, field.path, "") || fallback;
    });
    setColorDraft(next);
    setPaletteDraft(paletteFromForm(form));
  }, [colorFields, form, sectionId]);

  const updateVisual = (patch) => {
    const template = updateSegmentVisualStyle(form.theme?.template, sectionId, patch);
    onCommit("theme.template", template);
  };

  const setPaletteColor = (index, value) => {
    setPaletteDraft((previous) => {
      const next = [...previous];
      next[index] = value;
      setColorDraft((current) => ({
        ...current,
        ...paletteChanges(sectionId, next),
      }));
      return next;
    });
  };

  const applyColors = () => onCommitMany({
    ...paletteChanges(sectionId, paletteDraft),
    ...colorDraft,
  });

  const randomizeColors = () => {
    const randomized = randomizedReadablePalette(paletteDraft);
    const changes = paletteChanges(sectionId, randomized);
    setPaletteDraft(randomized);
    setColorDraft((current) => ({ ...current, ...changes }));
    onCommitMany(changes);
  };

  const resetSectionColors = () => {
    if (["about", "highlights", "contact"].includes(sectionId)) {
      const base = `appearance.${sectionId}`;
      return onCommitMany({
        [`${base}.backgroundColor`]: "",
        [`${base}.surfaceColor`]: "",
        [`${base}.accentColor`]: "",
        [`${base}.titleColor`]: "",
        [`${base}.textColor`]: "",
      });
    }
    if (sectionId === "spotlight") {
      return onCommitMany({
        "catalog.spotlight.primaryColor": form.theme.primaryColor || COLOR_DEFAULTS.primaryColor,
        "catalog.spotlight.secondaryColor": form.theme.secondaryColor || COLOR_DEFAULTS.secondaryColor,
      });
    }
    return onCommitMany(
      Object.fromEntries(
        Object.entries(COLOR_DEFAULTS).map(([key, value]) => [`theme.${key}`, value])
      )
    );
  };

  const setVisible = (next) => {
    if (sectionId === "spotlight") {
      onCommitMany({
        "sections.showProducts": true,
        "sections.showSpotlight": next,
        "catalog.spotlight.enabled": next,
      });
      return;
    }
    if (["featured", "brands", "categories"].includes(sectionId)) {
      onCommitMany({ "sections.showProducts": true, [visibilityPath]: next });
      return;
    }
    if (visibilityPath) onCommit(visibilityPath, next);
  };

  const drawerPaper = mobile
    ? { borderRadius: "20px 20px 0 0", maxHeight: "88vh" }
    : { width: 410, maxWidth: "92vw" };

  return (
    <Drawer
      anchor={mobile ? "bottom" : "right"}
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{ sx: drawerPaper }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: mobile ? "auto" : "100%" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ px: 2, pt: 1.7, pb: 1.1 }}>
          <Box>
            <Typography variant="overline" sx={{ color: "#F35A3A", fontWeight: 950, letterSpacing: 1 }}>
              EDITAR SEGMENTO
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 950, color: "#19242F", lineHeight: 1.1 }}>
              {label}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={saving} aria-label="Cerrar opciones">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          variant="scrollable"
          scrollButtons={false}
          sx={{ px: 1, minHeight: 44, borderBottom: "1px solid rgba(25,36,47,.09)" }}
        >
          <Tab icon={<TextFieldsRoundedIcon />} iconPosition="start" label="Textos" sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }} />
          <Tab icon={<PaletteRoundedIcon />} iconPosition="start" label="Colores" sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }} />
          <Tab icon={<TuneRoundedIcon />} iconPosition="start" label="Estilo" sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }} />
          <Tab icon={<FormatSizeRoundedIcon />} iconPosition="start" label="Letra" sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }} />
          <Tab icon={<ImageRoundedIcon />} iconPosition="start" label="Imagen" sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }} />
        </Tabs>

        <Box sx={{ p: 2, overflowY: "auto", flex: 1, bgcolor: "#F7F8FA" }}>
          {INDEX_TAB[tab] === "content" ? (
            <Stack spacing={1.5}>
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                También podés tocar directamente un texto sobre la página. Este botón abre todos los campos del segmento juntos.
              </Alert>
              <Button
                variant="contained"
                size="large"
                startIcon={<TextFieldsRoundedIcon />}
                onClick={() => onOpenTexts(sectionId)}
                sx={{ borderRadius: 3, py: 1.35, bgcolor: "#19242F", textTransform: "none", fontWeight: 950 }}
              >
                Cambiar textos y botones
              </Button>
              {visibilityPath ? (
                <FormControlLabel
                  control={<Switch checked={visible} onChange={(event) => setVisible(event.target.checked)} />}
                  label={visible ? "Esta sección está visible" : "Esta sección está oculta"}
                  sx={{ m: 0, p: 1.2, borderRadius: 3, bgcolor: "#fff", border: "1px solid rgba(25,36,47,.09)" }}
                />
              ) : null}
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "colors" ? (
            <Stack spacing={1.5}>
              <Box sx={{ p: 1.35, bgcolor: "#fff", borderRadius: 3, border: "1px solid rgba(25,36,47,.09)" }}>
                <Typography sx={{ fontWeight: 950, color: "#19242F" }}>
                  Paleta de colores
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.62)" }}>
                  Pegá los cinco colores que te pasó tu diseñador. Después podés probar distintas combinaciones sin perder la paleta.
                </Typography>

                <Box sx={{ mt: 1.25, display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 1 }}>
                  {PALETTE_LABELS.map((paletteLabel, index) => (
                    <Stack
                      key={paletteLabel}
                      direction="row"
                      spacing={0.8}
                      alignItems="center"
                      sx={{ p: 0.8, borderRadius: 2.5, bgcolor: "#F7F8FA", border: "1px solid rgba(25,36,47,.08)" }}
                    >
                      <Box
                        component="input"
                        type="color"
                        aria-label={paletteLabel}
                        value={toColorInput(paletteDraft[index])}
                        onChange={(event) => setPaletteColor(index, event.target.value)}
                        sx={{ width: 40, height: 38, p: 0.2, border: "1px solid rgba(25,36,47,.15)", borderRadius: 2, bgcolor: "#fff", cursor: "pointer", flexShrink: 0 }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label={paletteLabel}
                        value={paletteDraft[index] || ""}
                        onChange={(event) => setPaletteColor(index, event.target.value)}
                        inputProps={{ maxLength: 18 }}
                      />
                    </Stack>
                  ))}
                </Box>
              </Box>

              {!(["about", "highlights", "contact", "spotlight"].includes(sectionId)) ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Estos colores son generales y pueden repetirse en otros segmentos para mantener una identidad consistente.
                </Alert>
              ) : null}
              {colorFields.map((field) => (
                <Stack key={field.path} direction="row" spacing={1} alignItems="center" sx={{ p: 1.1, bgcolor: "#fff", borderRadius: 3, border: "1px solid rgba(25,36,47,.09)" }}>
                  <Box
                    component="input"
                    type="color"
                    aria-label={field.label}
                    value={toColorInput(colorDraft[field.path])}
                    onChange={(event) => setColorDraft((previous) => ({ ...previous, [field.path]: event.target.value }))}
                    sx={{ width: 46, height: 42, p: 0.3, border: "1px solid rgba(25,36,47,.15)", borderRadius: 2, bgcolor: "#fff", cursor: "pointer" }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label={field.label}
                    value={colorDraft[field.path] || ""}
                    onChange={(event) => setColorDraft((previous) => ({ ...previous, [field.path]: event.target.value }))}
                  />
                </Stack>
              ))}
              <Stack direction="row" spacing={1}>
                <Button fullWidth variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={resetSectionColors} disabled={saving} sx={{ borderRadius: 3, textTransform: "none", fontWeight: 900 }}>
                  Restablecer
                </Button>
                <Button fullWidth variant="contained" onClick={applyColors} disabled={saving} sx={{ borderRadius: 3, bgcolor: "#F35A3A", textTransform: "none", fontWeight: 950 }}>
                  Aplicar colores
                </Button>
              </Stack>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<ShuffleRoundedIcon />}
                onClick={randomizeColors}
                disabled={saving}
                sx={{
                  borderRadius: 3,
                  py: 1.25,
                  bgcolor: "#19242F",
                  textTransform: "none",
                  fontWeight: 950,
                  "&:hover": { bgcolor: "#101820" },
                }}
              >
                Aleatorizar colores
              </Button>
              <Typography variant="caption" sx={{ textAlign: "center", color: "rgba(25,36,47,.58)" }}>
                Cada clic redistribuye los mismos cinco tonos y prioriza una combinación legible.
              </Typography>
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "style" ? (
            <Stack spacing={2}>
              <LevelSelector
                label="Aire y separación del segmento"
                value={visual.spacing}
                options={[{ value: 1, label: "Compacto" }, { value: 2, label: "Equilibrado" }, { value: 3, label: "Amplio" }]}
                onChange={(spacing) => updateVisual({ spacing })}
              />

              {sectionId === "navbar" ? (
                <LevelSelector
                  label="Diseño del encabezado"
                  value={Number(String(form.navigation?.style || "style-1").slice(-1)) || 1}
                  options={[{ value: 1, label: "Clásico" }, { value: 2, label: "Comercial" }, { value: 3, label: "Minimal" }]}
                  onChange={(value) => onCommit("navigation.style", `style-${value}`)}
                />
              ) : null}

              {sectionId === "footer" ? (
                <LevelSelector
                  label="Diseño del pie"
                  value={Number(String(form.footer?.style || "style-1").slice(-1)) || 1}
                  options={[{ value: 1, label: "Simple" }, { value: 2, label: "Completo" }, { value: 3, label: "Oscuro" }]}
                  onChange={(value) => onCommit("footer.style", `style-${value}`)}
                />
              ) : null}

              {sectionId === "about" ? (
                <LevelSelector
                  label="Ubicación de la imagen"
                  value={form.about?.layout === "image-right" ? 3 : 2}
                  options={[{ value: 2, label: "Imagen izquierda" }, { value: 3, label: "Imagen derecha" }]}
                  onChange={(value) => onCommit("about.layout", value === 3 ? "image-right" : "image-left")}
                />
              ) : null}

              {sectionId === "hero" ? (
                <LevelSelector
                  label="Oscuridad sobre la imagen"
                  value={String(form.hero?.overlay || "").includes(".82") ? 3 : String(form.hero?.overlay || "").includes(".48") ? 1 : 2}
                  options={[{ value: 1, label: "Suave" }, { value: 2, label: "Legible" }, { value: 3, label: "Intensa" }]}
                  onChange={(value) => onCommit("hero.overlay", value === 1 ? "linear-gradient(to bottom, rgba(0,0,0,.08), rgba(8,8,20,.48))" : value === 3 ? "linear-gradient(to bottom, rgba(0,0,0,.28), rgba(8,8,20,.82))" : "linear-gradient(to bottom, rgba(0,0,0,.14), rgba(8,8,20,.72))")}
                />
              ) : null}

              <Box>
                <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 900 }}>Redondez general de tarjetas y botones</Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={Number(form.theme?.borderRadius || 10) <= 5 ? 1 : Number(form.theme?.borderRadius || 10) >= 20 ? 3 : 2}
                  onChange={(_, next) => next && onCommit("theme.borderRadius", next === 1 ? 4 : next === 3 ? 24 : 10)}
                >
                  <ToggleButton value={1}>Recto</ToggleButton>
                  <ToggleButton value={2}>Suave</ToggleButton>
                  <ToggleButton value={3}>Redondo</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "type" ? (
            <Stack spacing={2}>
              <LevelSelector
                label="Tamaño de los títulos de este segmento"
                value={visual.titleSize}
                options={[{ value: 1, label: "Pequeño" }, { value: 2, label: "Normal" }, { value: 3, label: "Grande" }]}
                onChange={(titleSize) => updateVisual({ titleSize })}
              />
              <LevelSelector
                label="Fuerza visual de los títulos"
                value={visual.textStyle}
                options={[{ value: 1, label: "Suave" }, { value: 2, label: "Firme" }, { value: 3, label: "Impactante" }]}
                onChange={(textStyle) => updateVisual({ textStyle })}
              />
              <Divider />
              <Typography variant="body2" sx={{ fontWeight: 900 }}>Familia tipográfica de toda la página</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                {FONT_PRESETS.map((preset) => {
                  const selected = form.theme?.headingFontFamily === preset.heading && form.theme?.bodyFontFamily === preset.body;
                  return (
                    <Button
                      key={preset.id}
                      variant={selected ? "contained" : "outlined"}
                      onClick={() => onCommitMany({
                        "theme.fontFamily": preset.body,
                        "theme.headingFontFamily": preset.heading,
                        "theme.bodyFontFamily": preset.body,
                        "theme.googleFontsUrl": preset.url,
                      })}
                      sx={{ minHeight: 74, borderRadius: 3, textTransform: "none", fontWeight: 900, bgcolor: selected ? "#19242F" : "#fff" }}
                    >
                      <Stack>
                        <Typography sx={{ fontFamily: preset.heading, fontWeight: 900 }}>{preset.label}</Typography>
                        <Typography variant="caption" sx={{ opacity: .72 }}>{preset.heading} + {preset.body}</Typography>
                      </Stack>
                    </Button>
                  );
                })}
              </Box>
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                El tamaño se aplica solamente a este segmento. La familia tipográfica se mantiene global para que la página no parezca armada con partes distintas.
              </Alert>
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "image" ? (
            <Stack spacing={1.6}>
              <LevelSelector
                label="Tamaño visual de las imágenes de este segmento"
                value={visual.imageSize}
                options={[{ value: 1, label: "Pequeña" }, { value: 2, label: "Normal" }, { value: 3, label: "Grande" }]}
                onChange={(imageSize) => updateVisual({ imageSize })}
              />

              {imageFields.length ? imageFields.map((field) => (
                <Box key={field.path} sx={{ p: 1.2, borderRadius: 3, bgcolor: "#fff", border: "1px solid rgba(25,36,47,.09)" }}>
                  <EditableImage
                    value={getAtPath(form, field.path, "")}
                    label={field.label}
                    helperText="Podés reemplazarla o eliminarla sin salir del segmento."
                    height={150}
                    disabled={saving}
                    onUpload={(file) => onUploadImage(field.path, file, { sectionKey: sectionId, assetKey: field.key })}
                    onRemove={() => onRemoveImage(field.path)}
                  />
                  {field.positionPath ? (
                    <FormControl fullWidth size="small" sx={{ mt: 1.2 }}>
                      <InputLabel>Posición de la imagen</InputLabel>
                      <Select
                        label="Posición de la imagen"
                        value={getAtPath(form, field.positionPath, "center")}
                        onChange={(event) => onCommit(field.positionPath, event.target.value)}
                      >
                        <MenuItem value="center">Centro</MenuItem>
                        <MenuItem value="top">Arriba</MenuItem>
                        <MenuItem value="bottom">Abajo</MenuItem>
                        <MenuItem value="left">Izquierda</MenuItem>
                        <MenuItem value="right">Derecha</MenuItem>
                      </Select>
                    </FormControl>
                  ) : null}
                </Box>
              )) : (
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  Este segmento usa contenido automático y no tiene una imagen de fondo propia. Podés modificar su tamaño visual y sus colores sin alterar los productos reales.
                </Alert>
              )}
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
}
